/**
 * Admin Users Management Page
 * 
 * Main page for managing users - list, search, pagination, and user actions
 */

import { useState, useEffect } from 'react';
import { 
  Search, 
  ChevronDown, 
  Edit,
  Ban,
  Trash2,
  UserX,
  UserCheck,
  Eye,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { adminApi } from '../stores/adminStore';
import UserDetailsModal from '../components/UserDetailsModal';
import CreditEditModal from '../components/CreditEditModal';

interface User {
  id: string;
  name: string;
  email?: string;  // Make email optional
  credits: number;
  status: 'active' | 'suspended' | 'banned';
  created_at: string;
  last_login_at?: string;
  total_albums?: number;  // Changed from album_count to match backend
  total_assets?: number;  // Changed from asset_count to match backend
  profile_image?: string;
  suspended_by?: string;
  banned_by?: string;
  ban_reason?: string;
  ban_expires_at?: string;
  storage_used_bytes?: number;
  // Subscription/Membership fields
  current_package?: string;
  membership_expires_at?: string;
  subscription_id?: string;
  subscription_status?: string;
  next_billing_date?: string;
  free_account_status?: boolean;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function UsersPage() {
  
  // State management
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Modals
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [showCreditEdit, setShowCreditEdit] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Load users
  const loadUsers = async (resetPage = false) => {
    setLoading(true);
    setError(null);

    try {
      const currentPage = resetPage ? 1 : pagination.page;
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pagination.limit.toString(),
        sortBy,
        sortOrder
      });

      if (search.trim()) {
        params.append('search', search.trim());
      }

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await adminApi.get(`/admin/users?${params}`);
      const responseData = response.data.data || response.data;
      const { users: usersData, pagination: paginationData } = responseData;

      // Ensure users is always an array
      setUsers(Array.isArray(usersData) ? usersData : []);
      setPagination(resetPage ? { ...paginationData, page: 1 } : paginationData);
    } catch (error: any) {
      console.error('Failed to load users:', error);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Initial load and reload on filter changes
  useEffect(() => {
    loadUsers(true);
  }, [search, statusFilter, sortBy, sortOrder]);

  // Reload on page change
  useEffect(() => {
    if (pagination.page > 1) {
      loadUsers(false);
    }
  }, [pagination.page]);

  // User actions
  const handleUserAction = async (userId: string, action: string, data?: any) => {
    setActionLoading(userId);
    try {
      switch (action) {
        case 'suspend':
          await adminApi.put(`/admin/users/${userId}/status`, {
            status: 'suspended',
            reason: data?.reason || 'Administrative action'
          });
          break;
        case 'unsuspend':
          await adminApi.put(`/admin/users/${userId}/status`, {
            status: 'active'
          });
          break;
        case 'delete':
          if (confirm('Are you sure you want to delete this user account? This action cannot be undone.')) {
            await adminApi.delete(`/admin/users/${userId}`, {
              data: {
                reason: data?.reason || 'Administrative deletion',
                confirmDelete: true
              }
            });
          }
          break;
      }

      // Reload users after action
      await loadUsers();
    } catch (error: any) {
      console.error(`Failed to ${action} user:`, error);
      alert(`Failed to ${action} user: ${error.response?.data?.message || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreditUpdate = async (userId: string, creditData: any) => {
    try {
      await adminApi.put(`/admin/users/${userId}/credits`, creditData);
      await loadUsers(); // Reload to show updated credits
      setShowCreditEdit(false);
      setSelectedUser(null);
    } catch (error: any) {
      console.error('Failed to update credits:', error);
      throw error;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Active' },
      suspended: { color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle, label: 'Suspended' },
      banned: { color: 'bg-red-100 text-red-800', icon: Ban, label: 'Banned' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.active;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const getCreditColor = (credits: number) => {
    if (credits > 100) return 'text-green-600 font-semibold';
    if (credits >= 10) return 'text-yellow-600 font-semibold';
    return 'text-red-600 font-semibold';
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString || dateString.trim() === '') return 'Never';
    
    // Handle PostgreSQL timestamp without timezone (last_login_at)
    // Database stores UTC time but without timezone indicator
    // We need to explicitly treat it as UTC by appending 'Z'
    let isoString = dateString.trim();
    
    // Replace space with T for ISO format
    isoString = isoString.replace(' ', 'T');
    
    // If no timezone info (Z or +/-), append Z to treat as UTC
    if (!isoString.endsWith('Z') && !isoString.includes('+') && !/[+-]\d{2}:\d{2}$/.test(isoString)) {
      isoString = isoString + 'Z';
    }
    
    const date = new Date(isoString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return 'Never';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 168) return `${Math.floor(diffHours / 24)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">User Management</h1>
        <p className="text-gray-600">
          Manage user accounts, credits, and permissions. Total users: {pagination.total}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="banned">Banned</option>
            </select>
            <ChevronDown className="absolute right-2 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Sort */}
          <div className="relative">
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order as 'asc' | 'desc');
              }}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="created_at-desc">Newest First</option>
              <option value="created_at-asc">Oldest First</option>
              <option value="last_login_at-desc">Recent Login</option>
              <option value="credits-desc">Most Credits</option>
              <option value="credits-asc">Least Credits</option>
            </select>
            <ChevronDown className="absolute right-2 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => loadUsers()}
            className="mt-2 text-red-700 underline hover:no-underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Credits
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assets
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-500">Loading users...</span>
                    </div>
                  </td>
                </tr>
              ) : !users || users.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    No users found. Try adjusting your filters.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {user.profile_image ? (
                            <img 
                              className="h-10 w-10 rounded-full object-cover" 
                              src={user.profile_image} 
                              alt={user.name}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <span className="text-gray-600 font-medium text-sm">
                                {user.name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email || 'No email'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className={`text-lg font-bold ${getCreditColor(user.credits)}`}>
                          {user.credits?.toLocaleString() || 0}
                        </span>
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowCreditEdit(true);
                          }}
                          className="text-blue-600 hover:text-blue-700 p-1 rounded hover:bg-blue-50"
                          title="Edit credits"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(user.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.last_login_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <div className="font-medium text-gray-900">{user.total_albums || 0} albums</div>
                        <div className="text-gray-600">{user.total_assets || 0} images</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {/* View Details */}
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowUserDetails(true);
                          }}
                          className="text-gray-600 hover:text-gray-900 p-1 rounded hover:bg-gray-100"
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>

                        {/* Status Actions */}
                        {user.status === 'active' && (
                          <button
                            onClick={() => handleUserAction(user.id, 'suspend', { 
                              reason: prompt('Reason for suspension:') 
                            })}
                            disabled={actionLoading === user.id}
                            className="text-yellow-600 hover:text-yellow-700 p-1 rounded hover:bg-yellow-50"
                            title="Suspend user"
                          >
                            <UserX className="w-4 h-4" />
                          </button>
                        )}

                        {(user.status === 'banned' || user.status === 'suspended') && (
                          <button
                            onClick={() => handleUserAction(user.id, 'unsuspend')}
                            disabled={actionLoading === user.id}
                            className="text-green-600 hover:text-green-700 p-1 rounded hover:bg-green-50"
                            title="Unsuspend user"
                          >
                            <UserCheck className="w-4 h-4" />
                          </button>
                        )}

                        {/* Delete */}
                        <button
                          onClick={() => handleUserAction(user.id, 'delete', { 
                            reason: prompt('Reason for deletion:') 
                          })}
                          disabled={actionLoading === user.id}
                          className="text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-50"
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>

                        {actionLoading === user.id && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && pagination.totalPages > 1 && (
          <div className="bg-white px-6 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                disabled={pagination.page === pagination.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.total}</span> users
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                    disabled={pagination.page === pagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {selectedUser && showUserDetails && (
        <UserDetailsModal
          user={selectedUser as any}
          isOpen={showUserDetails}
          onClose={() => {
            setShowUserDetails(false);
            setSelectedUser(null);
          }}
          onUserUpdate={loadUsers}
        />
      )}

      {selectedUser && showCreditEdit && (
        <CreditEditModal
          user={selectedUser as any}
          isOpen={showCreditEdit}
          onClose={() => {
            setShowCreditEdit(false);
            setSelectedUser(null);
          }}
          onCreditUpdate={handleCreditUpdate}
        />
      )}
    </div>
  );
}